export type CalculatePositionParams = {
  index: number;
  total: number;
  radius: number;
  startAngle: number;
  spread: number;
};

export type CalculateRadiusParams = {
  optionCount: number;
  spread: number;
};

/**
 * Calculates the x,y position for an option based on its index in the radial layout.
 * For full circle (360°), divides by total to avoid overlap.
 * For partial arcs, divides by total-1 so endpoints are included.
 */
export const calculatePosition = ({
  index,
  total,
  radius,
  startAngle,
  spread,
}: CalculatePositionParams): { x: number; y: number } => {
  const isFullCircle = spread >= 360;
  const divisor = isFullCircle ? total : total - 1;
  const angleStep = total > 1 ? spread / divisor : 0;
  const angle = startAngle + index * angleStep;
  const radians = (angle * Math.PI) / 180;

  return {
    x: Math.cos(radians) * radius,
    y: Math.sin(radians) * radius,
  };
};

const MIN_RADIUS_FULL_CIRCLE = 70;
const MAX_RADIUS_FULL_CIRCLE = 120;
const MIN_RADIUS_PARTIAL = 80;
const MAX_RADIUS_PARTIAL = 160;
const OPTION_SIZE = 56;

/**
 * Calculates the optimal radius for the radial menu based on the number of options
 * and the angular spread. More options or tighter spreads require a larger radius
 * to prevent overlapping. Full circles can use a smaller radius since options
 * are more evenly distributed.
 */
export const calculateRadius = ({
  optionCount,
  spread,
}: CalculateRadiusParams): number => {
  const isFullCircle = spread >= 360;
  const minRadius = isFullCircle ? MIN_RADIUS_FULL_CIRCLE : MIN_RADIUS_PARTIAL;
  const maxRadius = isFullCircle ? MAX_RADIUS_FULL_CIRCLE : MAX_RADIUS_PARTIAL;

  if (optionCount <= 1) {
    return minRadius;
  }

  // Calculate the arc length per option based on spread
  const divisor = isFullCircle ? optionCount : optionCount - 1;
  const anglePerOption = spread / divisor;

  // Convert to radians for calculation
  const angleRadians = (anglePerOption * Math.PI) / 180;

  // Calculate minimum radius needed so adjacent options don't overlap
  // Using chord length formula: chord = 2 * radius * sin(angle/2)
  // We want chord >= OPTION_SIZE, so radius >= OPTION_SIZE / (2 * sin(angle/2))
  const minRadiusForSpacing = OPTION_SIZE / (2 * Math.sin(angleRadians / 2));

  // Clamp between min and max radius
  return Math.min(
    maxRadius,
    Math.max(minRadius, Math.ceil(minRadiusForSpacing)),
  );
};
